import { FCX } from "@datassential/platform-ui-lib";
import {
    ConsumerGroupsTableItem,
    useConsumerGroupsTableConfig,
    useGetSelectedChartMetricValue
} from "./ConsumerGroupsTableConfig";
import { ComponentProps, useEffect, useMemo } from "react";
import { getCopyTableData } from "@components/Table/TableUtils";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import { TableColumnsContext } from "@components/Table/TableColumns";
import { Table, VirtualizedTable } from "@components/Table";
import cn from "classnames";
import { useFilterSort } from "@hooks/useFilterSort";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { useConsumerGroupsContext, View } from "@pages/Profile/components/ConsumerGroups/ConsumerGroups";
import HorizontalBarChart from "@components/HorizontalBarChart";
import Chart from "@pages/Profile/components/ConsumerGroups/Chart";

const Common: FCX<{
    data: ConsumerGroupsTableItem[];
    isVirtualized?: boolean;
}> = ({
    data: rawData,
    isVirtualized,
    className,
    children,
    ...props

}) => {
    const {
        selectedMetric,
        selectedView,
        setSelectedMetric,
    } = useConsumerGroupsContext();

    const dataForSorting = useMemo(
        () => rawData.map((item) => ({
            ...item,
            ...item.metrics.reduce(
                (acc, metric, index) => ({
                    ...acc,
                    [`metric${index}`]: metric.numValue ?? metric.value,
                }),
                {}
            ),
        })),
        [rawData]
    );

    const selectedMetricKey = useMemo(
        () => {
            const metricIndex = rawData[0]?.metrics.findIndex(
                (metric) => metric.id === selectedMetric
            );

            return metricIndex !== -1 ? `metric${metricIndex}` : "baseSize";
        },
        [selectedMetric, rawData]
    );

    const {
        data,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(dataForSorting, selectedMetricKey as keyof ConsumerGroupsTableItem);
    
    useEffect(() => {
        if (sortingKey !== "baseSize" && !sortingKey.startsWith("metric")) return;

        if (selectedView === View.Table && sortingKey !== selectedMetricKey) {
            const metricIndex = +sortingKey?.replace("metric", "");
            const metricId = rawData[0]?.metrics?.[metricIndex]?.id ?? null;
            setSelectedMetric(metricId);
        }
    }, [selectedView, sortingKey, rawData, selectedMetricKey, setSelectedMetric]);

    const getTableConfig = useConsumerGroupsTableConfig(true);

    const tableProviderValue = useMemo(
        () => ({
            data: getTableConfig(data),
        }),
        [getTableConfig, data]
    );

    const getSelectedChartMetricValue = useGetSelectedChartMetricValue();

    const chartData = useMemo(
        (): ComponentProps<typeof HorizontalBarChart>['data'] => {
            return data.map((item) => ({
                id: item.id,
                label: <div data-tooltip-id={item.nameTooltipId}>{item.name}</div>,
                ...getSelectedChartMetricValue(item),
            }));
        },
        [data, getSelectedChartMetricValue]
    );

    const sortContextValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                {
                    id: "name",
                    name: "name",
                },
                {
                    id: "baseSize",
                    name: "baseSize",
                },
                {
                    id: "metric0",
                    name: "metric0",
                },
                {
                    id: "metric1",
                    name: "metric1",
                },
                {
                    id: "metric2",
                    name: "metric2",
                },
                {
                    id: "metric3",
                    name: "metric3",
                },
                {
                    id: "metric4",
                    name: "metric4",
                },
            ],
            setValue: ({ id, direction}) => {
                setSortingKey(id as keyof ConsumerGroupsTableItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const copyTableContent = useMemo(
        () => getCopyTableData(tableProviderValue.data, data.length),
        [tableProviderValue.data, data.length]
    );

    return (
        <>
            <AsideButtons>
                <CopyTableButton
                    data={copyTableContent}
                    testId="buttonProfileCGCopyTable"
                />
            </AsideButtons>
            {selectedView === View.Table && (
                <TableSortingContext.Provider value={sortContextValue}>
                    <TableColumnsContext.Provider value={tableProviderValue}>
                        {isVirtualized ? (
                            <div className={cn("ProfileConsumerGroups__fixed-table", className)} {...props}>
                                <VirtualizedTable
                                    className="ProfileCgVirtualizedTable"
                                    rowsCount={data.length}
                                    testId="tableProfileCGVirtualized"
                                />
                            </div>
                        ) : (
                            <div className={className} {...props}>
                                <Table
                                    rowsCount={data.length}
                                    testId="tableProfileCG"
                                />
                            </div>
                        )}
                    </TableColumnsContext.Provider>
                </TableSortingContext.Provider>
            )}
            {selectedView === View.Chart && (
                <TableSortingContext.Provider value={sortContextValue}>
                    <Chart
                        data={chartData}
                        forceUpdate
                        selectedMetricKey={selectedMetricKey}
                        enableSorting={true}
                        testId="blockProfileCGChart"
                    />
                </TableSortingContext.Provider>
            )}
            {children}
        </>
    );
};

export default Common;